/**
 * useRazorpay
 * -----------
 * Loads the Razorpay checkout script on-demand and provides an `openCheckout`
 * function that:
 *   1. Calls the backend to create a subscription → gets { subscription_id, key_id }
 *   2. Opens the Razorpay modal
 *   3. On payment.success, calls backend verify endpoint (server-side sig + amount check)
 *   4. Redirects to /app/billing?success=true
 *   5. On failure, calls onError(message)
 *
 * Usage:
 *   const { openCheckout, loading } = useRazorpay({ onError });
 *   <Button onClick={() => openCheckout('pro')}>Subscribe ₹499/mo</Button>
 */

import { useState, useCallback } from 'react';
import { billingApi } from '../services/api';

const RAZORPAY_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';

function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (window.Razorpay) { resolve(true); return; }
        const script = document.createElement('script');
        script.src = RAZORPAY_SCRIPT;
        script.onload  = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
}

export default function useRazorpay({ onError } = {}) {
    const [loading, setLoading] = useState(false);

    const openCheckout = useCallback(async (planId, { userName = '', userEmail = '', userContact = '' } = {}) => {
        setLoading(true);
        try {
            // 1. Load script
            const scriptLoaded = await loadRazorpayScript();
            if (!scriptLoaded) {
                throw new Error('Failed to load Razorpay checkout. Check your internet connection.');
            }

            // 2. Create subscription on backend
            const { subscription_id, key_id } = await billingApi.createRazorpaySubscription(planId);

            // 3. Open Razorpay modal
            await new Promise((resolve, reject) => {
                const rzp = new window.Razorpay({
                    key:              key_id,
                    subscription_id:  subscription_id,
                    name:             'Socialixer',
                    description:      'AI Social Media Automation',
                    image:            '/logo192.png',
                    currency:         'INR',
                    prefill: {
                        name:    userName,
                        email:   userEmail,
                        contact: userContact,
                    },
                    theme: { color: '#6366f1' },

                    handler: async (response) => {
                        // 4. Server-side verification (signature + amount + currency + plan derivation)
                        try {
                            await billingApi.verifyRazorpayPayment(
                                response.razorpay_payment_id,
                                response.razorpay_subscription_id,
                                response.razorpay_signature,
                            );
                            // 5. Success — same URL pattern as Stripe
                            window.location.href = '/app/billing?success=true';
                            resolve();
                        } catch (verifyErr) {
                            reject(new Error(verifyErr.message || 'Payment verification failed'));
                        }
                    },

                    modal: {
                        ondismiss: () => {
                            // User closed modal without paying — not an error
                            resolve();
                        },
                    },
                });

                rzp.on('payment.failed', (response) => {
                    reject(new Error(
                        response?.error?.description || 'Payment failed. Please try again.'
                    ));
                });

                rzp.open();
            });
        } catch (err) {
            const msg = err.message || 'Razorpay checkout failed. Please try again.';
            if (onError) onError(msg);
        } finally {
            setLoading(false);
        }
    }, [onError]);

    return { openCheckout, loading };
}
